import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner, timeAgo } from '../components/common'
import { Endpoint, WindowPicker, useFlowWindow } from '../components/flows'
import { DataTable, type Column } from '../components/table'
import type { EntryPoint, ServiceEntry } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/** La stessa chiave usata come `rowKey`: indirizzo, protocollo, porta. */
function entryKey(e: EntryPoint): string {
  return `${e.address}-${e.protocol}-${e.port}`
}

/**
 * Il nome proposto per un servizio costruito da questi ingressi.
 *
 * Il nome è la parte che una persona correggerà comunque: quello che conta è
 * che **non sia vuoto**, perché un campo vuoto in cima a un modulo fa
 * abbandonare il modulo. Si prende il nome del CI se c'è — è quello che chi
 * guarda ha in mente — altrimenti l'indirizzo, che è sempre disponibile.
 */
function suggestName(entries: EntryPoint[]): string {
  if (entries.length === 0) return ''
  const first = entries[0]
  const base = first.ci_name ?? first.address
  if (entries.length === 1) return `${base} · ${first.label}`
  // Con più ingressi il nome del primo sarebbe fuorviante: si dice quanti sono.
  return t('{nome} e altri {n}', { nome: base, n: entries.length - 1 })
}

/**
 * Quanti ingressi accetta un servizio.
 *
 * Rispecchia `MAX_ENTRIES` in `app/schemas/business_service.py`, che resta
 * l'autorità — qui serve per **dirlo prima**: scegliere quaranta righe e
 * scoprire il limite da un errore del server significa rifare la scelta, e la
 * scelta è il lavoro. Una prova nel Control Plane controlla che i due numeri
 * non divergano.
 */
const MAX_ENTRIES = 32

/** Gli ingressi nel formato che il servizio si aspetta. */
function toServiceEntries(entries: EntryPoint[]): ServiceEntry[] {
  return entries.map((e) => ({
    // Si dichiara **sia** il CI sia l'indirizzo. Il CI è l'ancoraggio che
    // sopravvive a un cambio di indirizzo; l'indirizzo è ciò che permette al
    // servizio di restare definito anche se quel CI viene fuso o rimosso.
    ci_id: e.ci_id,
    address: e.address,
    port: e.port,
    protocol: e.protocol,
    label: e.label,
  }))
}

/**
 * Che cosa espone davvero questa rete.
 *
 * È la domanda a cui una CMDB compilata a mano non sa rispondere: un servizio
 * dimenticato su una macchina che nessuno amministra non compare in nessun
 * inventario dichiarato, ma compare qui — perché qualcuno lo usa.
 *
 * Ogni riga porta la propria **classificazione e il motivo**, e non è pedanteria.
 * NetFlow registra entrambe le direzioni di una conversazione: una parte di ciò
 * che «riceve» è la porta effimera di una connessione in uscita. La distinzione è
 * un giudizio su due indizi, non un fatto, e mostrare solo la conclusione
 * renderebbe impossibile capire su quale indizio ha sbagliato quando sbaglia.
 */
export default function FlowEntryPoints() {
  const [window, setWindow] = useFlowWindow()
  const [showEphemeral, setShowEphemeral] = useState(false)
  // Gli ingressi scelti si tengono per **chiave**, non per indice: filtri,
  // ordinamento e cambio di finestra rimescolano le righe di continuo, e una
  // selezione per posizione seguirebbe la riga sbagliata senza dirlo.
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const entryPoints = useAsync(
    // Si chiede il massimo che il server concede: filtri e ordinamento agiscono
    // sulle righe caricate, quindi caricarne poche renderebbe un filtro una
    // domanda su un campione — e il risultato si leggerebbe come completo.
    () => api.entryPoints({ window, limit: 500, include_ephemeral: showEphemeral }),
    [window, showEphemeral],
  )

  const rows = entryPoints.data ?? []
  const selected = rows.filter((e) => chosen.has(entryKey(e)))

  const toggle = (e: EntryPoint) => {
    const key = entryKey(e)
    const next = new Set(chosen)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setChosen(next)
  }

  // La colonna di scelta si costruisce qui e non fra le costanti: dipende dallo
  // stato, e una colonna dichiarata fuori resterebbe con la spunta della prima
  // resa.
  const columns: Column<EntryPoint>[] = useMemo(
    () => [
      {
        key: 'choose',
        header: '',
        searchable: false,
        value: (e: EntryPoint) => (chosen.has(entryKey(e)) ? 1 : 0),
        render: (e: EntryPoint) => (
          <input
            type="checkbox"
            checked={chosen.has(entryKey(e))}
            onChange={() => toggle(e)}
            aria-label={t('Scegli {indirizzo} {servizio}', { indirizzo: e.address, servizio: e.label })}
          />
        ),
      },
      ...entryColumns(),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chosen],
  )

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Entry point')}</h1>
          <p className="page__subtitle">
            {t('Identifica gli endpoint di rete, rileva indirizzi IP, porte e servizi esposti, le sorgenti che vi si connettono e le relative dipendenze applicative.')}
          </p>
        </div>
        <WindowPicker value={window} onChange={setWindow} />
      </header>

      <Card title={t('Servizi osservati')}>
        <ErrorBanner error={entryPoints.error} />

        {entryPoints.loading && !entryPoints.data ? (
          <Spinner />
        ) : !entryPoints.data?.length ? (
          <EmptyState
            title={t('Nessun servizio osservato')}
            hint={t("Servono flussi nella finestra scelta. Se il traffico c'è ma qui non compare nulla, allarga la finestra: un servizio si riconosce anche dal fatto che ricorre.")}
          />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={entryKey}
            isSelected={(e) => chosen.has(entryKey(e))}
            initialSort={{ key: 'connections', desc: true }}
            sourceNote={t('Filtri e ordinamento agiscono sui {n} servizi caricati per questa finestra, ordinati dal server per numero di mittenti.', { n: rows.length })}
          />
        )}

        {/* Un filtro che non si può disattivare è indistinguibile da un dato che
            manca: «perché questo non compare?» deve avere una risposta. */}
        <div className="checkbox" style={{ marginTop: 12 }}>
          <input
            id="ep-ephemeral"
            type="checkbox"
            checked={showEphemeral}
            onChange={(e) => setShowEphemeral(e.target.checked)}
          />
          <label htmlFor="ep-ephemeral">
            {t('Mostra anche il traffico di ritorno')}
            <small>
              {t('Le porte alte con un solo mittente sono quasi sempre la direzione di ritorno di una connessione in uscita, non un servizio. Sono nascoste perché in un elenco di servizi esposti sarebbero la maggioranza delle righe.')}
            </small>
          </label>
        </div>
      </Card>

      {selected.length > 0 && (
        <>
          {/* Il ponte verso la mappa di correlazione, dagli ingressi scelti.
              La mappa parte da UN indirizzo — è la sua natura: un seme e il
              suo vicinato — quindi con ingressi su più indirizzi si offre un
              pulsante per ciascuno invece di sceglierne uno in silenzio. */}
          <div className="banner banner--info">
            {t('Mostra in mappa:')}{' '}
            {[...new Set(selected.map((e) => e.address))].map((addr) => (
              <Link
                key={addr}
                className="chip"
                style={{ cursor: 'pointer' }}
                to={`/flows/map?seed=${encodeURIComponent(addr)}`}
              >
                {addr} →
              </Link>
            ))}
            {new Set(selected.map((e) => e.address)).size > 1 && (
              <small style={{ display: 'block', marginTop: 4 }}>
                {t('Una mappa per indirizzo: la correlazione parte da un seme solo.')}
              </small>
            )}
          </div>

          <NewServiceFromEntries entries={selected} onCancel={() => setChosen(new Set())} />
        </>
      )}
    </>
  )
}

/**
 * Un business service costruito dagli ingressi osservati.
 *
 * ## Perché da qui e non dalla pagina dei servizi
 *
 * Definire un servizio vuol dire dire **da dove ci si entra**, e quella
 * risposta è in questa pagina: gli indirizzi e le porte che ricevono
 * connessioni sono già lì, misurati. Dalla pagina dei servizi lo stesso lavoro
 * si fa a memoria — si ricopia un indirizzo letto altrove — ed è il punto in
 * cui un servizio nasce con l'ingresso sbagliato e nessuno se ne accorge
 * finché la mappa non esce vuota.
 *
 * Si dichiara **sia il CI sia l'indirizzo**: il CI è l'ancoraggio che
 * sopravvive a un cambio di indirizzo, l'indirizzo tiene in piedi la
 * definizione anche se quel CI viene fuso o rimosso.
 */
function NewServiceFromEntries({
  entries,
  onCancel,
}: {
  entries: EntryPoint[]
  onCancel: () => void
}) {
  const navigate = useNavigate()
  const [name, setName] = useState(suggestName(entries))
  const [criticality, setCriticality] = useState('medium')
  const [owner, setOwner] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // Il nome proposto segue la scelta finché nessuno lo tocca: aggiungere un
  // ingresso dopo aver scritto un nome non deve cancellare ciò che si è
  // scritto, ma un campo che resta fermo sul primo suggerimento mentre la
  // scelta cambia è altrettanto sbagliato.
  const [touched, setTouched] = useState(false)
  const proposed = suggestName(entries)
  const value = touched ? name : proposed

  async function create() {
    setBusy(true)
    setError(null)
    try {
      const service = await api.createService({
        name: value.trim(),
        criticality,
        owner: owner.trim() || null,
        entries: toServiceEntries(entries),
      })
      navigate(`/services/${service.id}`)
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuovo business service da {n} ingressi', { n: entries.length })}>
      <ErrorBanner error={error} />

      {/* The chosen entries, as a recap: the full table with its tools is above. */}
      <DataTable
        rows={entries}
        columns={sceltiColumns()}
        rowKey={(e) => entryKey(e)}
        strumenti={false}
      />

      {/*
        Un ingresso classificato «traffico di ritorno» non è un servizio
        esposto: è la porta effimera di una connessione in uscita. Sceglierlo si
        può — il riconoscimento è un giudizio e sbaglia — ma va detto, perché un
        servizio definito su una porta effimera produce una mappa che cambia a
        ogni finestra senza una ragione visibile.
      */}
      {entries.some((e) => e.classification === 'ephemeral') && (
        <div className="banner banner--info">
          {t("Fra gli ingressi scelti c'è del traffico di ritorno: è la porta effimera di una connessione in uscita, non un servizio esposto. Se è voluto va bene, ma la mappa del servizio cambierà a ogni finestra.")}
        </div>
      )}

      <div className="row" style={{ marginTop: 16 }}>
        <div className="field">
          <label htmlFor="svc-name">{t('Nome')}</label>
          <input
            id="svc-name"
            value={value}
            onChange={(e) => {
              setTouched(true)
              setName(e.target.value)
            }}
          />
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="svc-crit">{t('Criticità')}</label>
          <select id="svc-crit" value={criticality} onChange={(e) => setCriticality(e.target.value)}>
            <option value="critical">{t('Critico')}</option>
            <option value="high">{t('Alta')}</option>
            <option value="medium">{t('Media')}</option>
            <option value="low">{t('Bassa')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="svc-owner">{t('Responsabile')}</label>
          <input id="svc-owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        </div>
      </div>

      {entries.length > MAX_ENTRIES && (
        <div className="banner banner--info">
          {t('Un servizio accetta al massimo {max} ingressi, e ne hai scelti {n}. Toglierne qualcuno non è una limitazione tecnica da aggirare: un servizio con quaranta porte di ingresso non è un servizio, è una rete — e la sua mappa lo mostrerebbe.', { max: MAX_ENTRIES, n: entries.length })}
        </div>
      )}

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={create} disabled={busy || !value.trim() || entries.length > MAX_ENTRIES}>
          {busy ? t('Creazione…') : t('Crea business service')}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          {t('Annulla la scelta')}
        </button>
      </div>
    </Card>
  )
}

function classificationLabel(c: EntryPoint['classification']): string {
  switch (c) {
    case 'service': return t('servizio')
    case 'candidate': return t('da confermare')
    case 'ephemeral': return t('traffico di ritorno')
    default: return c
  }
}

/**
 * Le colonne, dichiarate fuori dal componente.
 *
 * `value` è ciò su cui si ordina e si filtra, `render` ciò che si vede: sono
 * cose diverse e tenerle separate è la ragione per cui «connessioni» si ordina
 * come numero pur essendo mostrato con il separatore delle migliaia, e la
 * classificazione si filtra scrivendo «servizio» e non «service».
 */
const entryColumns = (): Column<EntryPoint>[] => [
  {
    key: 'destination',
    header: t('Destinazione'),
    // Il nome del CI se c'è, altrimenti l'indirizzo: si cerca con quello che si
    // ha in mente, e chi cerca «web-01» non conosce il suo indirizzo.
    value: (e) => `${e.ci_name ?? ''} ${e.address} ${e.ptr ?? ''} ${e.desc ?? ''}`.trim(),
    render: (e) => <Endpoint id={e.ci_id} label={e.ci_name ?? e.address} addr={e.address} ptr={e.ptr} desc={e.desc} />,
  },
  {
    key: 'service',
    header: t('Servizio'),
    value: (e) => e.label,
    render: (e) => <span className="mono">{e.label}</span>,
  },
  { key: 'port', header: t('Porta'), type: 'number', align: 'right', value: (e) => e.port },
  {
    key: 'connections',
    header: t('Connessioni'),
    type: 'number',
    align: 'right',
    value: (e) => e.connections,
    render: (e) => e.connections.toLocaleString(localeCorrente()),
  },
  {
    key: 'sources',
    header: t('Mittenti'),
    type: 'number',
    align: 'right',
    value: (e) => e.sources,
    render: (e) => e.sources.toLocaleString(localeCorrente()),
  },
  {
    key: 'classification',
    header: t('Riconoscimento'),
    value: (e) => `${classificationLabel(e.classification)} ${e.reason}`,
    render: (e) => (
      <>
        <span
          className={
            e.classification === 'service'
              ? 'chip chip--ok'
              : e.classification === 'ephemeral'
                ? 'chip chip--muted'
                : 'chip'
          }
        >
          {classificationLabel(e.classification)}
        </span>{' '}
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.reason}</span>
      </>
    ),
  },
  {
    key: 'last_seen',
    header: t('Ultimo'),
    type: 'date',
    value: (e) => e.last_seen,
    render: (e) => timeAgo(e.last_seen),
  },
]

/** The columns of the chosen entries in «New business service». */
const sceltiColumns = (): Column<EntryPoint>[] => [
  {
    key: 'destination',
    header: t('Destinazione'),
    value: (e) => `${e.ci_name ?? ''} ${e.address} ${e.ptr ?? ''} ${e.desc ?? ''}`.trim(),
    render: (e) => <Endpoint id={e.ci_id} label={e.ci_name ?? e.address} addr={e.address} ptr={e.ptr} desc={e.desc} />,
  },
  { key: 'service', header: t('Servizio'), mono: true, value: (e) => e.label },
  { key: 'classification', header: t('Riconoscimento'), value: (e) => classificationLabel(e.classification) },
]
